import { HttpErrorResponse } from '@angular/common/http';

import { Fragment, Slice } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable, Subscription } from 'rxjs';
import { Subject, withLatestFrom } from 'rxjs';
import { distinctUntilChanged, mergeMap, takeUntil, tap } from 'rxjs/operators';

import { generateUuid, MaxFileSize, validateMaxFileSize } from '@core/utils-legacy';
import type { TrackableImageFile, WysiwygUploader } from '@core/wysiwyg-legacy';
import { uploadFactory } from '@core/wysiwyg-legacy';

import { createImage } from './image-commands';
import type { ImagePluginEvent } from './image-events';
import { ImageSizeExceeded, ImageUploadError } from './image-events';
import type { ImageNodeAttrs, ImageSchema } from './image-node';
import { ImageNode } from './image-node';
import { ImagePluginService } from './image-plugin.service';
import type {
  ImageMenuResult,
  ImageMenuController,
  CreateImageNodeView,
} from './image-plugin.types';
import { mapToTrackableImageFile } from './image-upload-helpers';
import type { EditorButton, EditorMode, RtPMPlugin } from '../../editor/types';
import { PendingEnd, PendingStart } from '../../events/pending';
import { AlignExtension } from '../../extensions/align';
import { nodeTypeSelectionObserver } from '../../helpers/node-type-selection-observer';
import { NodeName } from '../../schema/nodes';
import type {
  FigureContextAction,
  FigureContextMenuController,
} from '../context-menu/figure-context.types';

export class ImagePlugin implements RtPMPlugin {
  private readonly upload;
  private readonly MAX_IMAGE_SIZE = MaxFileSize.File50MB;

  public api: {
    imageService: ImagePluginService;
  };

  public buttons = {
    toggleImage: <EditorButton>{
      icon: 'image',
      tooltipText: 'wysiwyg.image',
      isActive: state => this.menuController.isCreateModeOpen,
      run: () => {
        this.menuController.open({
          cancel: () => this.menuController.close(),
          save: data => {
            this.createImage(data);
            this.menuController.close();
          },
        });
      },
    },
  };

  public imageNodeSelection$ = new Subject<number | null>();
  public eventStream$ = new Subject<ImagePluginEvent>();

  private destroy$ = new Subject<void>();
  private editorView: EditorView;
  private contextActionsSubscription: Subscription | null = null;
  private editorPlugin = new Plugin({
    appendTransaction: nodeTypeSelectionObserver(this.imageNodeSelection$, NodeName.Image),
    props: {
      nodeViews: {
        [ImageNode.nodeName]: (node, view, getPos) => {
          const onResize = (newWidth: number, position: number) => {
            this.api.imageService.updateWidth(position, newWidth, this.editorView);
            this.repositionContextMenu(position);
          };
          return this.imageNodeViewFactory(node, view, getPos, {
            nodeType: this.editorView.state.schema.nodes[NodeName.Image]!,
            onResize,
            isEditable: true,
          });
        },
      },
      // when pasting image we need to regenerate guid to keep it unique
      transformPasted: slice => {
        let newContent = Fragment.from();
        slice.content.forEach(node => {
          const { name } = node.type;
          const regenerateGuid = name === NodeName.Image;
          const newNode = regenerateGuid
            ? this.editorView.state.schema.nodes[name]!.create(
                { ...node.attrs, guid: generateUuid() },
                node.content,
              )
            : node;
          newContent = newContent.append(Fragment.from(newNode));
        });

        return new Slice(newContent, slice.openStart, slice.openEnd);
      },
    },
  });

  constructor(
    protected imageNodeViewFactory: CreateImageNodeView,
    protected menuController: ImageMenuController,
    protected contextMenuController: FigureContextMenuController,
    protected uploader: WysiwygUploader,
    protected scroll$: Observable<void>,
  ) {
    this.upload = uploadFactory(uploader);
    scroll$
      .pipe(withLatestFrom(this.imageNodeSelection$), takeUntil(this.destroy$))
      .subscribe(([_, position]) => {
        if (position) this.repositionContextMenu(position);
      });
    this.imageNodeSelection$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(position => this.updateContextMenu(position));
  }

  public createImage(data: ImageMenuResult) {
    if (!data.file) return;
    if (!validateMaxFileSize(data.file, this.MAX_IMAGE_SIZE)) {
      this.eventStream$.next(new ImageSizeExceeded());
      return;
    }
    mapToTrackableImageFile(data.file)
      .pipe(
        mergeMap(file => {
          const { alt, caption, href, targetBlank } = data;
          this.eventStream$.next(new PendingStart());
          const cmd = createImage(file, {
            alt,
            caption,
            href,
            targetBlank,
          });
          cmd(this.editorView.state, this.editorView.dispatch);

          return this.upload(file).pipe(
            tap(result => {
              if (result.error) {
                if (result.error instanceof HttpErrorResponse) {
                  this.eventStream$.next(new ImageUploadError(result.error.message));
                }
                this.api.imageService.deleteByGuid(file.id, this.editorView);
              } else {
                this.api.imageService.updateSrc(result, this.editorView);
              }
            }),
          );
        }),
      )
      .subscribe(() => {
        this.eventStream$.next(new PendingEnd());
      });
  }

  public beforeInit(mode: EditorMode, schema: ImageSchema) {
    this.api = {
      imageService: new ImagePluginService(),
    };
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins() {
    return [this.editorPlugin];
  }

  public destroy() {
    if (this.contextActionsSubscription) {
      this.contextActionsSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateContextMenu(position: number | null) {
    this.contextMenuController.close();
    if (this.contextActionsSubscription) {
      this.contextActionsSubscription.unsubscribe();
      this.contextActionsSubscription = null;
    }
    if (position === null) return;
    const originRect = this.getContextMenuRect(position);
    if (!originRect) return;

    const { action$ } = this.contextMenuController.open(originRect, {
      getAlign: () => {
        const { state } = this.editorView;
        return AlignExtension.getAlignAtPosition(state, position);
      },
    });
    this.contextActionsSubscription = action$.subscribe(action =>
      this.onContextAction(action, position),
    );
  }

  private onContextAction(action: FigureContextAction, position: number) {
    switch (action.type) {
      case 'edit':
        this.openEditMenu(position);
        break;
      case 'resetSize':
        this.api.imageService
          .resetSize(position, this.editorView)
          .subscribe(() => this.repositionContextMenu(position));
        break;
      case 'delete':
        this.api.imageService.delete(position, this.editorView);
        this.editorView.focus();
        break;
      case 'align':
        const cmd = AlignExtension.apply(action.value);
        const { state, dispatch } = this.editorView;
        cmd(state, dispatch);
        this.repositionContextMenu(position);
        break;
      default:
        break;
    }
  }

  private repositionContextMenu(position: number) {
    const originRect = this.getContextMenuRect(position);
    if (originRect) {
      this.contextMenuController.updatePosition(originRect);
    }
  }

  private getContextMenuRect(position: number): DOMRect | null {
    const nodeEl = <HTMLElement | null>this.editorView.nodeDOM(position);
    if (!nodeEl) return null;
    const imgRect = nodeEl.querySelector('img')?.getBoundingClientRect();
    const figureRect = nodeEl?.querySelector('figure')?.getBoundingClientRect();
    if (!imgRect || !figureRect) return null;
    return imgRect.width > figureRect.width ? figureRect : imgRect;
  }

  private openEditMenu(pos: number) {
    const imageNode = this.editorView.state.doc.nodeAt(pos);
    if (!imageNode?.attrs) return;
    const { src, alt, caption, href, targetBlank } = <ImageNodeAttrs>imageNode.attrs;
    const data = {
      src,
      alt,
      caption,
      href,
      targetBlank,
    };
    const callbacks = {
      cancel: () => {
        this.menuController.close();
      },
      save: (result: ImageMenuResult) => {
        if (result.file && !validateMaxFileSize(result.file, this.MAX_IMAGE_SIZE)) {
          this.eventStream$.next(new ImageSizeExceeded());
          result.src = data.src;
          result.file = undefined;
        }
        const replaceImage = this.api.imageService.updateAttrs(pos, data, result, this.editorView);
        if (replaceImage) {
          this.replaceImage(replaceImage, data.src);
        }
        this.menuController.close();
      },
      delete: () => {
        this.api.imageService.delete(pos, this.editorView);
        this.menuController.close();
        this.editorView.focus();
      },
    };
    this.menuController.open(callbacks, data);
  }

  private replaceImage(image: { guid: string; file: File }, originalSrc: string) {
    if (!validateMaxFileSize(image.file, this.MAX_IMAGE_SIZE)) {
      this.eventStream$.next(new ImageSizeExceeded());
      this.api.imageService.updateSrc(
        {
          id: image.guid,
          url: originalSrc,
        },
        this.editorView,
      );
      return;
    }
    this.eventStream$.next(new PendingStart());
    this.upload(<TrackableImageFile>{
      id: image.guid,
      file: image.file,
      url: URL.createObjectURL(image.file),
      type: 'image',
    }).subscribe(result => {
      if (result.error) {
        if (result.error instanceof HttpErrorResponse) {
          this.eventStream$.next(new ImageUploadError(result.error.message));
          this.api.imageService.updateSrc(
            {
              id: image.guid,
              url: originalSrc,
            },
            this.editorView,
          );
        }
      } else {
        this.api.imageService.updateSrc(result, this.editorView);
      }
      this.eventStream$.next(new PendingEnd());
    });
  }
}
